import { FC } from "react";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * The loading shape of FlowPagePaymentMethodAccordion: two labelled sections, each
 * a stack of collapsed method rows.
 *
 * Geometry is copied from the real collapsed row — rounded-2xl, px-5 py-4, the
 * 20px radio circle and 20px icon, gap-4 — so the rows do not move when the real
 * ones replace them.
 */

const MethodRowSkeleton: FC<{ withBadge?: boolean }> = ({ withBadge }) => (
  <div className="rounded-2xl border border-muted-foreground/20 bg-muted/10 px-5 py-4">
    <div className="flex w-full items-center gap-4 py-1">
      <Skeleton className="h-5 w-5 shrink-0 rounded-full" />
      <Skeleton className="h-5 w-5 shrink-0" />

      <div className="flex flex-1 min-w-0 items-center gap-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-24" />
      </div>

      {withBadge ? <Skeleton className="h-7 w-36 rounded-full mr-4" /> : null}
    </div>
  </div>
);

/**
 * Counts default to the common shape — one integrated method (credit card) and a
 * few manual ones — rather than to what this contract turns out to have, which is
 * the thing still loading.
 */
const PaymentMethodAccordionSkeleton: FC<{
  integratedRows?: number;
  manualRows?: number;
}> = ({ integratedRows = 1, manualRows = 3 }) => (
  <div className="space-y-8" aria-busy="true" aria-live="polite">
    <div>
      <Skeleton className="h-4 w-56 mb-4" />
      <div className="space-y-4">
        {Array.from({ length: integratedRows }).map((_, index) => (
          <MethodRowSkeleton key={index} withBadge={index === 0} />
        ))}
      </div>
    </div>

    <div>
      <Skeleton className="h-4 w-72 mb-4" />
      <div className="space-y-4">
        {Array.from({ length: manualRows }).map((_, index) => (
          <MethodRowSkeleton key={index} />
        ))}
      </div>
    </div>
  </div>
);

export default PaymentMethodAccordionSkeleton;
